import { useState, useEffect } from "react";
import axios from "axios";
import { useToast } from "@/hooks/use-toast";
import UserGreeting, { User } from "@/components/UserGreeting";
import UserSelector from "@/components/UserSelector";
import TimeDisplay from "@/components/TimeDisplay";
import CheckButton from "@/components/CheckButton";
import AttendanceStats, { AttendanceData } from "@/components/AttendanceStats";
import { ThemeToggle } from "@/components/ThemeToggle";
import BottomNavigation from "@/components/BottomNavigation";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

// Type for payload
type Payload = {
  mod: string;
  nokp: string;
  kod: string;
  lat: number;
  lng: number;
  ipaddress: string;
  deviceid: string;
  ptj: string;
  platform: string;
};

// Type for attendance API response
type AttendanceApiResponse = {
  MASAMASUK: string | null;
  MASAKELUAR: string | null;
};

// Type for attendance record API payload
type FetchRecordPayload = {
  mod: string;
  nokp: string;
};

type StoredAttendanceData = AttendanceData & {
  isCheckedIn: boolean;
  rawCheckInTime?: string;
  rawCheckOutTime?: string;
};

type ClockingSettings = {
  fastingStartDate: string;
  fastingEndDate: string;
  hanumPregnant: boolean;
};

const HANUM_ID = '970827035330';
const DEFAULT_CLOCKING_SETTINGS: ClockingSettings = {
  fastingStartDate: '',
  fastingEndDate: '',
  hanumPregnant: false,
};

const Index = () => {
  // Users configuration
  const users: User[] = [
    { id: '971116295077', name: 'Aisar' },
    { id: '970827035330', name: 'Hanum' }
  ];

  // State management
  const [selectedUser, setSelectedUser] = useState<User>(users[0]);
  const [activeTab, setActiveTab] = useState('clock');
  const [settings, setSettings] = useState<ClockingSettings>(DEFAULT_CLOCKING_SETTINGS);
  const [attendanceData, setAttendanceData] = useState<Record<string, StoredAttendanceData>>({
    971116295077: { isCheckedIn: false },
    970827035330: { isCheckedIn: false }
  });
  const [isLoading, setLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const { toast } = useToast();

  // Convert time format from HH:MM:SS to 12-hour format
  const convertTo12HourFormat = (time24: string): string => {
    if (!time24) return '';
    
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const minute = minutes;
    
    if (hour === 0) {
      return `12:${minute} AM`;
    } else if (hour < 12) {
      return `${hour}:${minute} AM`;
    } else if (hour === 12) {
      return `12:${minute} PM`;
    } else {
      return `${hour - 12}:${minute} PM`;
    }
  };

  // Fetch attendance records from API
  const fetchAttendanceRecord = async (nokp: string): Promise<AttendanceApiResponse | null> => {
    // Only make API calls in production/browser environment
    if (typeof window === 'undefined') {
      return null;
    }
    
    try {
      const payload: FetchRecordPayload = {
        mod: 'DASHBOARD',
        nokp: nokp
      };
  
      const response = await axios.post('https://www.unisza.edu.my/api2/iunisza/emasa2/tools', payload);
      return response.data[0];
    } catch (error) {
      console.error(`Error fetching attendance record for ${nokp}:`, error);
      return null;
    }
  };

  // Load attendance data for all users
  const loadAttendanceData = async () => {
    try {
      const newAttendanceData = { ...attendanceData };
      
      // Fetch data for all users
      for (const user of users) {
        const apiData = await fetchAttendanceRecord(user.id);
        
        if (apiData) {
          const checkInTime = apiData.MASAMASUK ? convertTo12HourFormat(apiData.MASAMASUK) : undefined;
          const checkOutTime = apiData.MASAKELUAR ? convertTo12HourFormat(apiData.MASAKELUAR) : undefined;
          const isCheckedIn = !!checkInTime && !checkOutTime;
          
          // Calculate total hours if both check-in and check-out exist
          const totalHours = checkInTime && checkOutTime 
            ? calculateTotalHours(checkInTime, checkOutTime)
            : undefined;

          newAttendanceData[user.id] = {
            ...newAttendanceData[user.id],
            isCheckedIn,
            checkInTime,
            checkOutTime,
            totalHours,
            rawCheckInTime: apiData.MASAMASUK || undefined,
            rawCheckOutTime: apiData.MASAKELUAR || undefined,
          };
        }
      }
      
      setAttendanceData(newAttendanceData);
      
      // Save updated data to localStorage
      try {
        localStorage.setItem('clockingAttendanceData', JSON.stringify(newAttendanceData));
      } catch (error) {
        console.error('Error saving data to localStorage:', error);
      }
      
    } catch (error) {
      console.error('Error loading attendance data:', error);
      toast({
        title: "Data Loading Error",
        description: "Failed to load attendance records from server.",
        variant: "destructive"
      });
    }
  };

  // Load data from localStorage first, then fetch from API
  useEffect(() => {
    const initializeData = async () => {
      setIsInitialLoading(true);
      
      // First, try to load from localStorage
      try {
        const savedData = localStorage.getItem('clockingAttendanceData');
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          setAttendanceData(parsedData);
        }

        const savedSettings = localStorage.getItem('clockingSettings');
        if (savedSettings) {
          const parsedSettings = JSON.parse(savedSettings);
          setSettings({ ...DEFAULT_CLOCKING_SETTINGS, ...parsedSettings });
        }
      } catch (error) {
        console.error('Error loading data from localStorage:', error);
      }
      
      // Then fetch fresh data from API (only in browser environment)
      if (typeof window !== 'undefined') {
        try {
          await loadAttendanceData();
        } catch (error) {
          console.error('Failed to load attendance data:', error);
          // Continue with localStorage data if API fails
        }
      }
      
      setIsInitialLoading(false);
    };

    initializeData();
  }, []);

  // Save data to localStorage whenever attendanceData changes (except during initial load)
  useEffect(() => {
    if (!isInitialLoading) {
      try {
        localStorage.setItem('clockingAttendanceData', JSON.stringify(attendanceData));
      } catch (error) {
        console.error('Error saving data to localStorage:', error);
      }
    }
  }, [attendanceData, isInitialLoading]);

  // Save settings to localStorage whenever settings change (except during initial load)
  useEffect(() => {
    if (!isInitialLoading) {
      try {
        localStorage.setItem('clockingSettings', JSON.stringify(settings));
      } catch (error) {
        console.error('Error saving settings to localStorage:', error);
      }
    }
  }, [settings, isInitialLoading]);

  // Helper functions - exactly the same as before
  const getCurrentTime = () => {
    return new Date().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const calculateTotalHours = (checkIn: string, checkOut: string) => {
    const parseTime = (timeStr: string) => {
      const [time, modifier] = timeStr.split(' ');
      let [hours] = time.split(':').map(Number);
      const [, minutes] = time.split(':').map(Number);
      if (modifier === 'PM' && hours !== 12) hours += 12;
      if (modifier === 'AM' && hours === 12) hours = 0;
      return hours * 60 + minutes;
    };

    const checkInMinutes = parseTime(checkIn);
    const checkOutMinutes = parseTime(checkOut);
    const diffMinutes = checkOutMinutes - checkInMinutes;
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const getLocalDateString = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isFastingDate = (date: Date, currentSettings: ClockingSettings) => {
    const today = getLocalDateString(date);
    const { fastingStartDate, fastingEndDate } = currentSettings;

    if (!fastingStartDate || !fastingEndDate) return false;
    if (fastingStartDate > fastingEndDate) return false;

    return today >= fastingStartDate && today <= fastingEndDate;
  };

  const getBreakMinutes = (isFasting: boolean) => isFasting ? 30 : 60;

  const getRequiredOfficeMinutes = (isThursday: boolean, isFasting: boolean, isPregnant: boolean) => {
    if (isThursday) {
      if (isFasting) return isPregnant ? 7 * 60 : 8 * 60;
      return isPregnant ? 7 * 60 + 30 : 8 * 60 + 30;
    }

    if (isFasting) return isPregnant ? 7 * 60 + 45 : 8 * 60 + 15;
    return isPregnant ? 7 * 60 + 45 : 8 * 60 + 45;
  };

  const getClockingSchedule = (user: User, date: Date, currentSettings: ClockingSettings) => {
    const isThursday = date.getDay() === 4;
    const isFasting = isFastingDate(date, currentSettings);
    const isPregnant = user.id === HANUM_ID && currentSettings.hanumPregnant;
    const officeMinutes = getRequiredOfficeMinutes(isThursday, isFasting, isPregnant);
    const breakMinutes = getBreakMinutes(isFasting);

    return {
      isThursday,
      isFasting,
      isPregnant,
      officeMinutes,
      breakMinutes,
      requiredWorkingMinutes: officeMinutes - breakMinutes,
    };
  };

  const parseApiTimeForDate = (timeString: string | undefined, date: Date) => {
    if (!timeString) return null;

    const [hours, minutes, seconds = '0'] = timeString.split(':');
    const hour = Number(hours);
    const minute = Number(minutes);
    const second = Number(seconds);

    if ([hour, minute, second].some(Number.isNaN)) return null;

    const parsedDate = new Date(date);
    parsedDate.setHours(hour, minute, second, 0);
    return parsedDate;
  };

  const getBreakOverlapMinutes = (start: Date, end: Date, isFasting: boolean) => {
    if (end <= start) return 0;

    const breakStart = new Date(start);
    breakStart.setHours(13, 0, 0, 0);

    const breakEnd = new Date(start);
    breakEnd.setHours(isFasting ? 13 : 14, isFasting ? 30 : 0, 0, 0);

    const overlapStart = Math.max(start.getTime(), breakStart.getTime());
    const overlapEnd = Math.min(end.getTime(), breakEnd.getTime());

    if (overlapEnd <= overlapStart) return 0;
    return Math.ceil((overlapEnd - overlapStart) / 60000);
  };

  const calculateWorkedMinutes = (checkIn: Date, checkOut: Date, isFasting: boolean) => {
    const elapsedMinutes = Math.floor((checkOut.getTime() - checkIn.getTime()) / 60000);
    return Math.max(0, elapsedMinutes - getBreakOverlapMinutes(checkIn, checkOut, isFasting));
  };

  const getEarliestCheckOutTime = (checkIn: Date, requiredWorkingMinutes: number, isFasting: boolean) => {
    let earliest = new Date(checkIn.getTime() + requiredWorkingMinutes * 60000);

    // Recalculate a few times because adding break overlap can move the time further into the break window.
    for (let i = 0; i < 3; i += 1) {
      const breakOverlap = getBreakOverlapMinutes(checkIn, earliest, isFasting);
      earliest = new Date(checkIn.getTime() + (requiredWorkingMinutes + breakOverlap) * 60000);
    }

    return earliest;
  };

  const formatDateTo12HourTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (remainingMinutes === 0) return `${hours} hr`;
    return `${hours} hr ${remainingMinutes} min`;
  };

  const updateSettings = (updates: Partial<ClockingSettings>) => {
    setSettings((currentSettings) => ({ ...currentSettings, ...updates }));
  };

  // Get current user's data
  const currentUserData = attendanceData[selectedUser.id];

  const todaySchedule = getClockingSchedule(selectedUser, new Date(), settings);
  const currentCheckInDate = parseApiTimeForDate(currentUserData?.rawCheckInTime, new Date());
  const earliestCheckOutTime = currentCheckInDate
    ? getEarliestCheckOutTime(currentCheckInDate, todaySchedule.requiredWorkingMinutes, todaySchedule.isFasting)
    : null;

  // Random IP generation
  const getRandomIp = () => {
    const arrIpAdd = ["115.164.169.156", "115.164.172.24", "115.164.177.160", "115.164.178.56", "115.164.179.239", "115.164.184.53", "115.164.184.86", "115.164.185.45", "115.164.187.225", "115.164.200.178", "115.164.200.72", "115.164.202.133", "115.164.202.245", "115.164.202.3", "115.164.206.149", "115.164.208.113", "115.164.211.248", "115.164.212.34", "115.164.216.99", "115.164.219.12", "115.164.219.122", "115.164.221.208", "115.164.42.190", "115.164.56.142", "115.164.60.203", "115.164.78.199", "115.164.87.108", "115.164.90.4", "115.164.95.125"];

    const randIndex = Math.floor(Math.random() * arrIpAdd.length);

    return arrIpAdd[randIndex];
  };

  // Random Lat/Lon generation
  const getRandomLatLon = (centerLat: number, centerLon: number, radiusInMeters: number) => {
    const y0 = centerLat;
    const x0 = centerLon;

    const rd = radiusInMeters / 111300;

    const u = Math.random();
    const v = Math.random();

    const w = rd * Math.sqrt(u);
    const t = 2 * Math.PI * v;
    const x = w * Math.cos(t);
    const y = w * Math.sin(t);

    const newX = x / Math.cos(y0);

    const finalLon = newX + x0;
    const finalLat = y + y0;

    return { latitude: finalLat, longitude: finalLon };
  };

  // Clocking API call
  const clocking = async (payload: Payload) => {
    return await emasa(payload);
  };

  // Emasa API call
  const emasa = async (payload: Payload) => {
    try {
      const response = await axios.post('https://www.unisza.edu.my/api2/iunisza/emasa', payload);
      return response.data[0].MESSAGE;
    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }
  };

  // Attendance toggle - updated to refresh data after clocking
  const handleAttendanceToggle = async () => {
    if (isLoading) return;

    setLoading(true);
    const currentTime = getCurrentTime();

    // Recompute time for accuracy
    const now = new Date();
    const nowHour = now.getHours();
    const nowMinute = now.getMinutes();
    const nowDay = now.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const isCheckingIn = !currentUserData.isCheckedIn;

    // Check day restriction first (no Fri/Sat)
    if (nowDay === 5 || nowDay === 6) {
      toast({
        title: "Day Restriction",
        description: "Check-in and check-out are not allowed on Friday or Saturday.",
        variant: "destructive"
      });
      setLoading(false);
      return;
    }

    if (isCheckingIn) {
      // Check-in: 7:00-8:30 AM
      const checkInValid = nowHour === 7 || (nowHour === 8 && nowMinute <= 30);
      if (!checkInValid) {
        toast({
          title: "Time Restriction",
          description: "Check-in is only allowed from 7:00 AM to 8:30 AM.",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }
    } else {
      const checkInDate = parseApiTimeForDate(currentUserData.rawCheckInTime, now);

      if (!checkInDate) {
        toast({
          title: "Check-in Time Missing",
          description: "Unable to verify working hours. Please refresh data first.",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      const schedule = getClockingSchedule(selectedUser, now, settings);
      const earliestCheckOut = getEarliestCheckOutTime(
        checkInDate,
        schedule.requiredWorkingMinutes,
        schedule.isFasting
      );
      const workedMinutes = calculateWorkedMinutes(checkInDate, now, schedule.isFasting);

      if (workedMinutes < schedule.requiredWorkingMinutes) {
        toast({
          title: "Working Hours Not Complete",
          description: `Check-out is allowed after ${formatDateTo12HourTime(earliestCheckOut)}. Current progress: ${formatDuration(workedMinutes)} / ${formatDuration(schedule.requiredWorkingMinutes)}.`,
          variant: "destructive"
        });
        setLoading(false);
        return;
      }
    }

    try {
      if (!currentUserData.isCheckedIn) {
        // Check in
        const { latitude: lat, longitude: lng } = selectedUser.id === '971116295077' 
          ? getRandomLatLon(5.398227, 103.079699, 12) 
          : getRandomLatLon(5.399026, 103.076889, 15);
        const ipaddress = getRandomIp();

        const payload: Payload = {
          mod: 'CLOCKING',
          nokp: selectedUser.id,
          kod: '1',
          lat: lat,
          lng: lng,
          ipaddress: ipaddress,
          deviceid: selectedUser.id === '971116295077' 
            ? 'BDB8C08E-7E59-4999-88A0-03FB0E4D2908' 
            : 'B396C0C1-1E88-4F38-8763-6747965A8E71',
          ptj: selectedUser.id === '971116295077' ? '22' : '13',
          platform: 'web',
        };

        await clocking(payload);

        toast({
          title: "Checked In Successfully!",
          description: `Welcome back ${selectedUser.name}! Checked in at ${currentTime}`,
        });
      } else {
        // Check out
        const { latitude: lat, longitude: lng } = selectedUser.id === '971116295077' 
          ? getRandomLatLon(5.398227, 103.079699, 12) 
          : getRandomLatLon(5.399026, 103.076889, 15);
        const ipaddress = getRandomIp();

        const payload: Payload = {
          mod: 'CLOCKING',
          nokp: selectedUser.id,
          kod: '2',
          lat: lat,
          lng: lng,
          ipaddress: ipaddress,
          deviceid: selectedUser.id === '971116295077' 
            ? 'BDB8C08E-7E59-4999-88A0-03FB0E4D2908' 
            : 'B396C0C1-1E88-4F38-8763-6747965A8E71',
          ptj: selectedUser.id === '971116295077' ? '22' : '13',
          platform: 'web',
        };

        await clocking(payload);

        toast({
          title: "Checked Out Successfully!",
          description: `See you tomorrow ${selectedUser.name}! Checked out at ${currentTime}`,
        });
      }
      
      // Refresh attendance data from API after successful clocking
      await loadAttendanceData();
      
    } catch (error) {
      console.error('Clocking error:', error);
      toast({
        title: "Clocking Error",
        description: "Failed to process your check-in/check-out.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Manual refresh function
  const handleRefresh = async () => {
    if (isLoading) return;
    
    setLoading(true);
    await loadAttendanceData();
    setLoading(false);
    
    toast({
      title: "Data Refreshed",
      description: "Attendance records have been updated.",
    });
  };

  // Greeting message - same logic as before
  const getGreetingMessage = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning! Have a great day";
    if (hour < 17) return "Good afternoon! How's your day going?";
    return "Good evening! Ready to check out?";
  };

  // Show loading spinner during initial load
  if (isInitialLoading) {
    return (
      <div className="h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading attendance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-background overflow-hidden">
      {/* Theme toggle for testing */}
      <ThemeToggle />
      
      {/* Mobile-first container */}
      <div className="max-w-md mx-auto bg-card shadow-soft h-full overflow-y-auto pb-28">
        {activeTab === 'clock' ? (
          <>
            {/* User greeting */}
            <UserGreeting 
              user={selectedUser} 
              message={getGreetingMessage()}
            />

            {/* User selector */}
            <UserSelector
              users={users}
              selectedUser={selectedUser}
              onUserSelect={setSelectedUser}
            />

            {/* Time display */}
            <TimeDisplay />

            {/* Check in/out button */}
            <CheckButton
              isCheckedIn={currentUserData.isCheckedIn}
              onToggle={handleAttendanceToggle}
              isLoading={isLoading}
            />

            {/* Attendance stats */}
            <AttendanceStats 
              checkInTime={currentUserData.checkInTime}
              checkOutTime={currentUserData.checkOutTime}
              totalHours={currentUserData.totalHours}
            />


            {/* Refresh button */}
            <div className="px-6 mt-4 mb-6">
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="w-full py-2 px-4 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                    Refreshing...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh Data
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          <div className="px-6 py-6 space-y-5">
            <div>
              <h1 className="text-2xl font-semibold text-time-display">Settings</h1>
              <p className="text-sm text-time-secondary">Fasting period and Hanum status settings.</p>
            </div>

            <div className="rounded-xl border-2 border-border bg-card p-4 shadow-soft space-y-4">
              <div>
                <h2 className="text-base font-semibold text-time-display">Fasting Dates</h2>
                <p className="text-xs text-time-secondary">Fasting mode is active when today is within this date range.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="fasting-start">Start date</Label>
                  <Input
                    id="fasting-start"
                    type="date"
                    value={settings.fastingStartDate}
                    onChange={(event) => updateSettings({ fastingStartDate: event.target.value })}
                    className="rounded-lg bg-card text-xs px-2"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fasting-end">End date</Label>
                  <Input
                    id="fasting-end"
                    type="date"
                    value={settings.fastingEndDate}
                    onChange={(event) => updateSettings({ fastingEndDate: event.target.value })}
                    className="rounded-lg bg-card text-xs px-2"
                  />
                </div>
              </div>

              {settings.fastingStartDate && settings.fastingEndDate && settings.fastingStartDate > settings.fastingEndDate && (
                <p className="text-sm text-destructive">Fasting start date must be before the end date.</p>
              )}
            </div>

            <div className="rounded-xl border-2 border-border bg-card p-4 shadow-soft">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="hanum-pregnant" className="text-base font-semibold text-time-display">
                    Allow Early Check-out for Pregnant
                  </Label>
                  <p className="text-xs text-time-secondary mt-1">This switch only affects Hanum's time calculation.</p>
                </div>
                <Switch
                  id="hanum-pregnant"
                  checked={settings.hanumPregnant}
                  onCheckedChange={(checked) => updateSettings({ hanumPregnant: checked })}
                />
              </div>
            </div>

            <div className="rounded-xl border-2 border-border bg-card p-4 shadow-soft space-y-3">
              <h2 className="text-base font-semibold text-time-display">Today's Summary</h2>
              <div className="flex items-center justify-between">
                <span className="text-sm text-time-secondary">Fasting</span>
                <span className="text-sm font-semibold text-time-display">{todaySchedule.isFasting ? 'Active' : 'Inactive'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-time-secondary">Allow Early Check-out for Pregnant</span>
                <span className="text-sm font-semibold text-time-display">{todaySchedule.isPregnant ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-time-secondary">Required duration</span>
                <span className="text-sm font-semibold text-time-display">{formatDuration(todaySchedule.officeMinutes)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <BottomNavigation 
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
    </div>
  );
};

export default Index;